import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { hasPermission } from '../../utils/permissions';

import { API_BASE_URL } from '../../config/api';

function FacilityCreate({ onNavigate }) {
  const { token, user } = useAuth();
  
  // Form fields
  const [name, setName] = useState('');
  const [facilityType, setFacilityType] = useState('accommodation');
  const [description, setDescription] = useState('');
  const [contactNumber, setContactNumber] = useState('');
  const [physicalAddress, setPhysicalAddress] = useState('');
  const [email, setEmail] = useState('');
  const [latitude, setLatitude] = useState(25.0);
  const [longitude, setLongitude] = useState(85.0);
  const [journeyId, setJourneyId] = useState('');
  const [organizationId, setOrganizationId] = useState('');
  const [routeId, setRouteId] = useState('');
  const [waypointId, setWaypointId] = useState('');

  // Commercial & Verification fields
  const [verified, setVerified] = useState(false);
  const [isSponsored, setIsSponsored] = useState(false);
  const [sponsorshipStartAt, setSponsorshipStartAt] = useState('');
  const [sponsorshipEndAt, setSponsorshipEndAt] = useState('');
  const [displayPriority, setDisplayPriority] = useState(0);
  const [discountCoupon, setDiscountCoupon] = useState('');
  const [discountType, setDiscountType] = useState('');
  const [discountValue, setDiscountValue] = useState('');
  const [couponValidUntil, setCouponValidUntil] = useState('');

  // Dropdowns lists
  const [journeys, setJourneys] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [waypoints, setWaypoints] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const canVerify = hasPermission(user, 'facility.verify');
  const canSponsor = hasPermission(user, 'admin') || hasPermission(user, 'super_admin');

  // Fetch dropdown data on mount
  useEffect(() => {
    const fetchDropdownData = async () => {
      const headers = { 'Authorization': `Bearer ${token}` };
      try {
        const [jRes, oRes, rRes] = await Promise.all([
          fetch(`${API_BASE_URL}/journeys`, { headers }),
          fetch(`${API_BASE_URL}/organizations/`, { headers }),
          fetch(`${API_BASE_URL}/routes`, { headers })
        ]);
        
        if (jRes.ok) setJourneys(await jRes.json());
        if (oRes.ok) setOrganizations(await oRes.json());
        if (rRes.ok) setRoutes(await rRes.json());
      } catch (e) {
        console.error('Failed to fetch form dropdowns', e);
      }
    };
    fetchDropdownData();
  }, [token]);

  // Fetch waypoints when route changes
  useEffect(() => {
    if (!routeId) {
      setWaypoints([]);
      setWaypointId('');
      return;
    }
    const fetchWaypoints = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/routes/${routeId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const routeData = await res.json();
          setWaypoints(routeData.waypoints || []);
        }
      } catch (e) {
        console.error('Failed to fetch route waypoints', e);
      }
    };
    fetchWaypoints();
  }, [routeId, token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('Facility name is required');
      return;
    }
    if (latitude === '' || longitude === '') {
      setError('Latitude and Longitude are required');
      return;
    }

    setLoading(true);
    setError(null);

    const payload = {
      name,
      facility_type: facilityType,
      description: description || null,
      contact_number: contactNumber || null,
      physical_address: physicalAddress || null,
      email: email || null,
      latitude: parseFloat(latitude),
      longitude: parseFloat(longitude),
      journey_id: journeyId || null,
      organization_id: organizationId || null,
      waypoint_id: waypointId || null,
      verified,
      is_sponsored: isSponsored,
      sponsorship_start_at: sponsorshipStartAt ? new Date(sponsorshipStartAt).toISOString() : null,
      sponsorship_end_at: sponsorshipEndAt ? new Date(sponsorshipEndAt).toISOString() : null,
      display_priority: parseInt(displayPriority) || 0,
      discount_coupon: discountCoupon || null,
      discount_type: discountType || null,
      discount_value: discountValue !== '' ? parseFloat(discountValue) : null,
      coupon_valid_until: couponValidUntil ? new Date(couponValidUntil).toISOString() : null
    };

    try {
      const res = await fetch(`${API_BASE_URL}/facilities/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        onNavigate('facilities');
      } else {
        const errData = await res.json();
        setError(errData.detail || 'Failed to create facility');
      }
    } catch (e) {
      setError('Network error. Failed to create facility.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card" style={{ maxWidth: '800px', margin: '0 auto' }}>
      <h3 style={{ marginBottom: '24px' }}>Register New Facility</h3>
      
      {error && (
        <div style={{ padding: '12px', background: '#FFEBEE', color: '#C62828', borderRadius: '6px', marginBottom: '20px' }}>
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
          <div>
            <label className="block text-sm font-medium mb-1" style={{ display: 'block', marginBottom: '6px', fontWeight: 600 }}>Facility Name *</label>
            <input 
              type="text" 
              className="input-field" 
              value={name} 
              onChange={(e) => setName(e.target.value)} 
              style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ccc' }}
              placeholder="e.g. Shiv Mandir Medical Camp"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1" style={{ display: 'block', marginBottom: '6px', fontWeight: 600 }}>Facility Type *</label>
            <select 
              value={facilityType} 
              onChange={(e) => setFacilityType(e.target.value)}
              style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ccc', height: '40px' }}
            >
              <option value="accommodation">Accommodation</option>
              <option value="food">Food Camp</option>
              <option value="medical">Medical Camp</option>
              <option value="others">Others</option>
            </select>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600 }}>E-mail Address</label>
            <input 
              type="email" 
              value={email} 
              onChange={(e) => setEmail(e.target.value)} 
              style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ccc' }}
              placeholder="contact@facility.org"
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600 }}>Physical Address (Landmark)</label>
            <input 
              type="text" 
              value={physicalAddress} 
              onChange={(e) => setPhysicalAddress(e.target.value)} 
              style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ccc' }}
              placeholder="Shop 12, Tower Chowk, Deoghar"
            />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600 }}>Latitude *</label>
            <input 
              type="number" 
              step="any" 
              value={latitude} 
              onChange={(e) => setLatitude(e.target.value)}
              style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ccc' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600 }}>Longitude *</label>
            <input 
              type="number" 
              step="any" 
              value={longitude} 
              onChange={(e) => setLongitude(e.target.value)}
              style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ccc' }}
            />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600 }}>Contact Number</label>
            <input 
              type="text" 
              value={contactNumber} 
              onChange={(e) => setContactNumber(e.target.value)}
              style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ccc' }}
              placeholder="e.g. +91 9876543210"
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600 }}>Partner Organization</label>
            <select 
              value={organizationId} 
              onChange={(e) => setOrganizationId(e.target.value)}
              style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ccc', height: '40px' }}
            >
              <option value="">None / Independent</option>
              {organizations.map(o => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </select>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600 }}>Select Route (to view waypoints)</label>
            <select 
              value={routeId} 
              onChange={(e) => setRouteId(e.target.value)}
              style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ccc', height: '40px' }}
            >
              <option value="">Select Route...</option>
              {routes.map(r => (
                <option key={r.id} value={r.id}>{r.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600 }}>Select Route Point (Waypoint Mapping)</label>
            <select 
              value={waypointId} 
              onChange={(e) => setWaypointId(e.target.value)}
              style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ccc', height: '40px' }}
              disabled={!routeId}
            >
              <option value="">Select Waypoint...</option>
              {waypoints.map(w => (
                <option key={w.id} value={w.id}>{w.waypoint_name || `Seq ${w.sequence_no}`}</option>
              ))}
            </select>
          </div>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600 }}>Associated Yatra Season (Journey)</label>
          <select 
            value={journeyId} 
            onChange={(e) => setJourneyId(e.target.value)}
            style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ccc', height: '40px' }}
          >
            <option value="">Select Journey...</option>
            {journeys.map(j => (
              <option key={j.id} value={j.id}>{j.name}</option>
            ))}
          </select>
        </div>

        <div style={{ marginBottom: '24px' }}>
          <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600 }}>Description / Notes</label>
          <textarea 
            value={description} 
            onChange={(e) => setDescription(e.target.value)}
            style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ccc', minHeight: '80px' }}
            placeholder="Provide additional details about services, timings, etc."
          />
        </div>

        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button 
            type="button" 
            className="btn-outline" 
            onClick={() => onNavigate('facilities')}
            disabled={loading}
          >
            Cancel
          </button>
          <button 
            type="submit" 
            className="btn-primary"
            disabled={loading}
          >
            {loading ? 'Creating...' : 'Register Facility'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default FacilityCreate;
